import { callGPTAPI } from '../utils/api'
import { CrmContact } from './crmApi'
import { audiencesCreatorApi, AudienceRecord } from './creatorApi'

// ── Types ──

export interface DiscoveredField {
  key: string
  label: string
  type: 'text' | 'select' | 'boolean' | 'number'
  values?: string[]
  trueValues?: string[]
}

export interface AudienceRule {
  id: string
  field: string
  fieldSource: 'standard' | 'payload'
  operator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'in'
    | 'is_true'
    | 'is_false'
    | 'is_empty'
    | 'is_not_empty'
  value: string | string[]
}

export interface SavedAudience {
  id: string
  name: string
  description: string
  team: string
  region?: string
  rules: AudienceRule[]
  logic: 'AND' | 'OR'
  createdAt: string
  updatedAt: string
  lastCount?: number
}

// ── Standard CRM Fields ──

export interface StandardFieldDef {
  key: string
  label: string
  type: 'text' | 'select' | 'boolean'
  operators: AudienceRule['operator'][]
}

export const STANDARD_FIELDS: StandardFieldDef[] = [
  {
    key: 'Team',
    label: 'Team',
    type: 'text',
    operators: ['equals', 'not_equals', 'contains', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Region',
    label: 'Region',
    type: 'text',
    operators: ['equals', 'not_equals', 'contains', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Sub_Region',
    label: 'Sub Region',
    type: 'text',
    operators: ['equals', 'not_equals', 'contains', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Tags',
    label: 'Tags',
    type: 'text',
    operators: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Mailing_List',
    label: 'Mailing List',
    type: 'text',
    operators: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Categories',
    label: 'Categories',
    type: 'text',
    operators: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Status',
    label: 'Status',
    type: 'text',
    operators: ['equals', 'not_equals', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Form_Name',
    label: 'Form Name',
    type: 'text',
    operators: ['equals', 'not_equals', 'contains', 'is_empty', 'is_not_empty'],
  },
  {
    key: 'Email',
    label: 'Email',
    type: 'text',
    operators: [
      'equals',
      'contains',
      'not_contains',
      'is_empty',
      'is_not_empty',
    ],
  },
]

// ── Dynamic CRM Field Discovery ──

/** Fields to exclude from dynamic discovery (internal/system fields) */
const EXCLUDED_CRM_KEYS = new Set([
  'id',
  'Created_Time',
  'Modified_Time',
  'Created_By',
  'Modified_By',
  'Owner',
  '$currency_symbol',
  '$state',
  '$process_flow',
  '$approved',
  '$approval',
  '$editable',
  '$review_process',
  '$review',
  '$orchestration',
  '$in_merge',
  '$approval_state',
  '$pathfinder',
  '$sharing_permission',
  'Record_Image',
  'Last_Activity_Time',
  'Unsubscribed_Mode',
  'Unsubscribed_Time',
  'Exchange_Rate',
  'Currency',
  'Locked__s',
])

/** Known human-readable labels for common CRM fields */
const KNOWN_LABELS: Record<string, string> = {
  Email: 'Email',
  First_Name: 'First Name',
  Last_Name: 'Last Name',
  Full_Name: 'Full Name',
  Team: 'Team',
  Region: 'Region',
  Sub_Region: 'Sub Region',
  Tags: 'Tags',
  Mailing_List: 'Mailing List',
  Categories: 'Categories',
  Status: 'Status',
  Form_Name: 'Form Name',
  Form_Payload: 'Form Payload',
  Phone: 'Phone',
  Mobile: 'Mobile',
  Title: 'Title',
  Department: 'Department',
  Bucket_ID: 'Bucket ID',
  Bucket_ID_Expires_At: 'Bucket ID Expires At',
}

/** Fields that are comma-separated multi-value and need special operators */
const MULTI_VALUE_FIELDS = new Set(['Tags', 'Mailing_List', 'Categories'])

/** Max unique values before a field is treated as free-text instead of select */
const SELECT_THRESHOLD = 20

/**
 * Convert a CRM field key to a human-readable label.
 * Uses known labels first, then converts snake_case/camelCase to Title Case.
 */
function fieldKeyToLabel(key: string): string {
  if (KNOWN_LABELS[key]) return KNOWN_LABELS[key]
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

/**
 * Discover CRM fields dynamically from loaded contact records.
 * Scans all contacts to find available fields, determines their types,
 * and collects unique values for select-type fields.
 */
export function discoverCrmFieldsFromContacts(
  contacts: CrmContact[],
): StandardFieldDef[] {
  if (contacts.length === 0) return STANDARD_FIELDS // Fallback to hardcoded

  // Collect all unique keys and sample values
  const fieldValues: Record<string, Set<string>> = {}
  const fieldNonEmpty: Record<string, number> = {}

  for (const contact of contacts) {
    for (const [key, value] of Object.entries(contact)) {
      // Skip excluded/internal fields
      if (EXCLUDED_CRM_KEYS.has(key)) continue
      if (key.startsWith('$')) continue

      // Skip complex objects (Owner, lookup objects) — not directly filterable
      if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        (value as any).id
      ) {
        continue
      }

      if (!fieldValues[key]) {
        fieldValues[key] = new Set()
        fieldNonEmpty[key] = 0
      }

      if (value != null && String(value).trim() !== '') {
        fieldNonEmpty[key]++
        // For comma-separated fields, split and add individual values
        if (MULTI_VALUE_FIELDS.has(key) && typeof value === 'string') {
          value.split(',').forEach((v) => {
            const trimmed = v.trim()
            if (trimmed) fieldValues[key].add(trimmed)
          })
        } else {
          fieldValues[key].add(String(value).trim())
        }
      }
    }
  }

  // Skip Form_Payload — that's handled by AI discovery separately
  delete fieldValues['Form_Payload']
  delete fieldNonEmpty['Form_Payload']

  // Build field definitions
  const fields: StandardFieldDef[] = []

  // Sort keys: put commonly-used fields first, then alphabetically
  const priorityOrder = [
    'Email',
    'Full_Name',
    'First_Name',
    'Last_Name',
    'Team',
    'Region',
    'Sub_Region',
    'Tags',
    'Mailing_List',
    'Categories',
    'Status',
    'Form_Name',
  ]

  const sortedKeys = Object.keys(fieldValues).sort((a, b) => {
    const aIdx = priorityOrder.indexOf(a)
    const bIdx = priorityOrder.indexOf(b)
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
    if (aIdx !== -1) return -1
    if (bIdx !== -1) return 1
    return a.localeCompare(b)
  })

  for (const key of sortedKeys) {
    const uniqueValues = fieldValues[key]
    const nonEmptyCount = fieldNonEmpty[key] || 0

    // Skip fields that are never populated
    if (nonEmptyCount === 0) continue

    // Determine field type and operators
    let type: 'text' | 'select' | 'boolean' = 'text'
    let operators: AudienceRule['operator'][]

    if (MULTI_VALUE_FIELDS.has(key)) {
      // Multi-value fields get contains/not_contains operators
      type = 'text'
      operators = ['contains', 'not_contains', 'is_empty', 'is_not_empty']
    } else if (
      uniqueValues.size <= SELECT_THRESHOLD &&
      uniqueValues.size >= 2
    ) {
      // Small set of unique values → treat as select
      type = 'select'
      operators = ['equals', 'not_equals', 'in', 'is_empty', 'is_not_empty']
    } else {
      // Free text
      type = 'text'
      operators = [
        'equals',
        'not_equals',
        'contains',
        'not_contains',
        'is_empty',
        'is_not_empty',
      ]
    }

    fields.push({
      key,
      label: fieldKeyToLabel(key),
      type,
      operators,
    })
  }

  return fields
}

// ── Operators metadata ──

export const OPERATOR_LABELS: Record<AudienceRule['operator'], string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  in: 'is one of',
  is_true: 'is checked / true',
  is_false: 'is unchecked / false',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
}

export function getOperatorsForFieldType(
  type: 'text' | 'select' | 'boolean' | 'number',
): AudienceRule['operator'][] {
  switch (type) {
    case 'boolean':
      return ['is_true', 'is_false']
    case 'select':
      return ['equals', 'not_equals', 'in', 'is_empty', 'is_not_empty']
    case 'number':
      return ['equals', 'not_equals', 'is_empty', 'is_not_empty']
    case 'text':
    default:
      return [
        'equals',
        'not_equals',
        'contains',
        'not_contains',
        'is_empty',
        'is_not_empty',
      ]
  }
}

// ── GPT Field Discovery ──

const DISCOVERY_INSTRUCTIONS = `You analyze form submission data and identify filterable fields. Given sample form payloads as JSON objects, identify all unique filterable fields across all payloads.

Return ONLY valid JSON (no markdown, no explanation) in this exact format:
{"fields":[{"key":"fieldName","label":"Human Readable Label","type":"text|select|boolean|number","values":["val1","val2"],"trueValues":["yes","true"]}]}

Rules:
1. For boolean fields (checkboxes, yes/no answers), set type to "boolean" and list all values that mean true in "trueValues" (e.g., ["yes", "true", "1", "checked", "on"]).
2. For fields with a small set of distinct values (under 15 unique values), set type to "select" and list all unique values in "values".
3. For free-text fields (names, phone numbers, addresses, etc.), set type to "text".
4. For numeric fields, set type to "number".
5. Make labels human-readable: camelCase "firstName" becomes "First Name", snake_case "phone_number" becomes "Phone Number".
6. EXCLUDE email fields (key containing "email" or "e_mail") since email is already a standard filter.
7. EXCLUDE any field that looks like an internal ID or timestamp.
8. Include ALL fields you find across ALL the sample payloads, even if a field only appears in some payloads.`

export async function discoverPayloadFields(
  payloads: string[],
): Promise<DiscoveredField[]> {
  if (payloads.length === 0) return []

  // Parse and deduplicate payloads
  const parsed: Record<string, any>[] = []
  const seen = new Set<string>()

  for (const raw of payloads) {
    if (!raw) continue
    try {
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw
      const key = JSON.stringify(Object.keys(obj).sort())
      if (!seen.has(key) || parsed.length < 20) {
        seen.add(key)
        parsed.push(obj)
      }
    } catch {
      // skip unparseable payloads
    }
  }

  if (parsed.length === 0) return []

  // Take up to 20 samples
  const samples = parsed.slice(0, 20)

  try {
    const result = await callGPTAPI(
      DISCOVERY_INSTRUCTIONS,
      JSON.stringify(samples),
    )

    // Parse GPT response — it might have markdown code fences
    let cleaned = result.trim()
    if (cleaned.startsWith('```')) {
      cleaned = cleaned
        .replace(/^```(?:json)?\s*\n?/, '')
        .replace(/\n?```\s*$/, '')
    }

    const data = JSON.parse(cleaned)

    if (data.fields && Array.isArray(data.fields)) {
      return data.fields.map((f: any) => ({
        key: f.key || '',
        label: f.label || f.key || '',
        type: f.type || 'text',
        values: f.values || undefined,
        trueValues: f.trueValues || undefined,
      }))
    }

    return []
  } catch (err) {
    console.error('GPT field discovery failed:', err)
    throw new Error(
      `Failed to analyze form data: ${err instanceof Error ? err.message : 'Unknown error'}`,
    )
  }
}

// ── Audience Storage (localStorage) ──

const STORAGE_KEY = 'configconsole_audiences'

function loadAll(): SavedAudience[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveAll(audiences: SavedAudience[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(audiences))
}

export const audienceStorage = {
  list: (team: string): SavedAudience[] => {
    return loadAll().filter((a) => a.team === team)
  },

  get: (id: string): SavedAudience | null => {
    return loadAll().find((a) => a.id === id) || null
  },

  save: async (
    audience: SavedAudience,
  ): Promise<{ synced: boolean; error?: string }> => {
    const all = loadAll()
    const idx = all.findIndex((a) => a.id === audience.id)
    if (idx >= 0) {
      all[idx] = { ...audience, updatedAt: new Date().toISOString() }
    } else {
      all.push({
        ...audience,
        createdAt: audience.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    }
    saveAll(all)

    // Sync to Creator — await so caller knows if it succeeded
    try {
      await syncAudienceToCreator(audience)
      return { synced: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      console.warn('Failed to sync audience to Creator:', message)
      return { synced: false, error: message }
    }
  },

  delete: async (id: string): Promise<{ synced: boolean; error?: string }> => {
    saveAll(loadAll().filter((a) => a.id !== id))

    // Sync delete to Creator — await so caller knows if it succeeded
    try {
      await deleteAudienceFromCreator(id)
      return { synced: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      console.warn('Failed to delete audience from Creator:', message)
      return { synced: false, error: message }
    }
  },

  /**
   * Load audiences from Creator and merge into localStorage.
   * Creator is the source of truth; localStorage is the fast cache.
   * Call this on app init or when entering the audiences module.
   */
  syncFromCreator: async (team: string): Promise<SavedAudience[]> => {
    try {
      const creatorRecords = await audiencesCreatorApi.list()
      const teamRecords = creatorRecords.filter((r) => r.Team === team)

      if (teamRecords.length === 0) {
        // No Creator records — localStorage is the only source
        return loadAll().filter((a) => a.team === team)
      }

      // Convert Creator records to SavedAudience format
      const creatorAudiences = teamRecords
        .map(creatorRecordToAudience)
        .filter(Boolean) as SavedAudience[]

      // Merge: Creator wins for any ID that exists in both
      const all = loadAll()
      const localOnlyAudiences = all.filter(
        (local) =>
          local.team !== team ||
          !creatorAudiences.some((ca) => ca.id === local.id),
      )

      const merged = [...localOnlyAudiences, ...creatorAudiences]
      saveAll(merged)

      return creatorAudiences
    } catch (err) {
      console.warn('Failed to sync audiences from Creator:', err)
      // Fallback to localStorage
      return loadAll().filter((a) => a.team === team)
    }
  },
}

// ── Creator Sync Helpers ──

function audienceToCreatorRecord(
  audience: SavedAudience,
): Partial<Omit<AudienceRecord, 'ID'>> {
  return {
    Audience_ID: audience.id,
    Audience_Name: audience.name,
    Description: audience.description || '',
    Team: audience.team,
    Region: audience.region || '',
    Rules_JSON: JSON.stringify(audience.rules),
    Logic: audience.logic,
    Last_Count: audience.lastCount || 0,
  }
}

function creatorRecordToAudience(record: AudienceRecord): SavedAudience | null {
  try {
    const rules: AudienceRule[] = record.Rules_JSON
      ? JSON.parse(record.Rules_JSON)
      : []
    return {
      id: record.Audience_ID || record.ID,
      name: record.Audience_Name || '',
      description: record.Description || '',
      team: record.Team || '',
      region: record.Region || undefined,
      rules,
      logic: (record.Logic as 'AND' | 'OR') || 'AND',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastCount: record.Last_Count || undefined,
    }
  } catch {
    console.warn('Failed to parse Creator audience record:', record.ID)
    return null
  }
}

async function syncAudienceToCreator(audience: SavedAudience): Promise<void> {
  const payload = audienceToCreatorRecord(audience)
  try {
    // Check if it already exists in Creator
    const existing = await audiencesCreatorApi.findByAudienceId(audience.id)
    if (existing) {
      await audiencesCreatorApi.update(existing.ID, payload)
    } else {
      await audiencesCreatorApi.create(payload)
    }
  } catch (err) {
    // Don't throw — Creator sync is best-effort
    console.warn('Creator audience sync failed:', err)
  }
}

async function deleteAudienceFromCreator(audienceId: string): Promise<void> {
  try {
    const existing = await audiencesCreatorApi.findByAudienceId(audienceId)
    if (existing) {
      await audiencesCreatorApi.delete(existing.ID)
    }
  } catch (err) {
    console.warn('Creator audience delete failed:', err)
  }
}

// ── Contact Filtering ──

function parsePayloadSafe(raw?: string): Record<string, any> | null {
  if (!raw) return null
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return null
  }
}

/**
 * Extract a field value from a contact, returning it as a string.
 * Handles:
 * - Standard CRM fields (direct properties on the contact object)
 * - Payload fields (parsed from Form_Payload JSON)
 * - Lookup objects ({name, id}) → extracts name
 * - Arrays → joins with commas
 * - Booleans/numbers → stringified
 * - Nested objects → JSON stringified
 */
function getFieldValue(
  contact: CrmContact,
  rule: AudienceRule,
): string | undefined {
  if (rule.fieldSource === 'payload') {
    const payload = parsePayloadSafe((contact as any).Form_Payload)
    if (!payload) return undefined
    const val = payload[rule.field]
    if (val == null) return undefined
    // Handle arrays (e.g., multi-select checkboxes)
    if (Array.isArray(val)) {
      const items = val
        .map((item) => {
          if (item == null) return ''
          if (typeof item === 'object')
            return item.name || item.label || JSON.stringify(item)
          return String(item)
        })
        .filter(Boolean)
      return items.length > 0 ? items.join(', ') : undefined
    }
    // Handle nested objects
    if (typeof val === 'object') {
      if (val.name) return val.name
      if (val.label) return val.label
      if (val.value != null) return String(val.value)
      return JSON.stringify(val)
    }
    // Handle booleans explicitly to preserve "true"/"false"
    if (typeof val === 'boolean') return val ? 'true' : 'false'
    return String(val)
  }

  // Standard field
  const val = (contact as any)[rule.field]
  if (val == null) return undefined
  // Handle lookup objects (e.g., Owner: {name: "...", id: "..."})
  if (typeof val === 'object' && !Array.isArray(val) && val.name)
    return val.name
  // Handle arrays (multiselect lookups that weren't normalized)
  if (Array.isArray(val)) {
    const items = val
      .map((item: any) => {
        if (typeof item === 'string') return item
        return item?.name || item?.Name || item?.display_value || ''
      })
      .filter(Boolean)
    return items.length > 0 ? items.join(', ') : undefined
  }
  return String(val)
}

/**
 * Check if a field is a comma-separated multi-value CRM field.
 * Only applies to STANDARD fields — payload fields are never comma-separated CRM fields.
 */
function isCommaSeparatedField(rule: AudienceRule): boolean {
  return (
    rule.fieldSource === 'standard' &&
    ['Tags', 'Mailing_List', 'Categories'].includes(rule.field)
  )
}

/**
 * Split a comma-separated string into trimmed, non-empty items.
 */
function splitCommaValues(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function matchesRule(contact: CrmContact, rule: AudienceRule): boolean {
  const rawValue = getFieldValue(contact, rule)

  switch (rule.operator) {
    case 'is_empty':
      return !rawValue || rawValue.trim() === ''

    case 'is_not_empty':
      return !!rawValue && rawValue.trim() !== ''

    case 'is_true': {
      if (!rawValue) return false
      const lower = rawValue.toLowerCase().trim()
      return ['yes', 'true', '1', 'checked', 'on', 'y'].includes(lower)
    }

    case 'is_false': {
      if (!rawValue) return true // null/undefined/empty treated as false
      const lower = rawValue.toLowerCase().trim()
      return ['no', 'false', '0', 'unchecked', 'off', 'n', ''].includes(lower)
    }

    case 'equals': {
      if (!rawValue) return false
      const target = Array.isArray(rule.value) ? rule.value[0] : rule.value
      if (!target && target !== '0') return false // No target value set — rule is incomplete, don't match
      // For comma-separated CRM fields, check if any item exactly equals the target
      if (isCommaSeparatedField(rule)) {
        const items = splitCommaValues(rawValue)
        return items.some((item) => item.toLowerCase() === target.toLowerCase())
      }
      // For number fields, try numeric comparison first
      const numRaw = Number(rawValue)
      const numTarget = Number(target)
      if (
        !isNaN(numRaw) &&
        !isNaN(numTarget) &&
        rawValue.trim() !== '' &&
        target.trim() !== ''
      ) {
        return numRaw === numTarget
      }
      return rawValue.toLowerCase() === target.toLowerCase()
    }

    case 'not_equals': {
      const target = Array.isArray(rule.value) ? rule.value[0] : rule.value
      if (!target && target !== '0') return true // No target value set — rule is incomplete, everything "not equals" nothing
      if (!rawValue) return true // null doesn't equal anything
      // For comma-separated CRM fields, check that NO item exactly equals the target
      if (isCommaSeparatedField(rule)) {
        const items = splitCommaValues(rawValue)
        return !items.some(
          (item) => item.toLowerCase() === target.toLowerCase(),
        )
      }
      // For number fields, try numeric comparison first
      const numRaw = Number(rawValue)
      const numTarget = Number(target)
      if (
        !isNaN(numRaw) &&
        !isNaN(numTarget) &&
        rawValue.trim() !== '' &&
        target.trim() !== ''
      ) {
        return numRaw !== numTarget
      }
      return rawValue.toLowerCase() !== target.toLowerCase()
    }

    case 'contains': {
      if (!rawValue) return false
      const target = Array.isArray(rule.value) ? rule.value[0] : rule.value
      if (!target) return false // No target value — incomplete rule
      // For comma-separated CRM fields (Tags, Mailing_List, Categories):
      // Use EXACT item match (case-insensitive) — "Newsletter" should NOT match "Newsletter 2"
      if (isCommaSeparatedField(rule)) {
        const items = splitCommaValues(rawValue)
        return items.some((item) => item.toLowerCase() === target.toLowerCase())
      }
      // For all other fields: substring match
      return rawValue.toLowerCase().includes(target.toLowerCase())
    }

    case 'not_contains': {
      if (!rawValue) return true // No value means it doesn't contain anything
      const target = Array.isArray(rule.value) ? rule.value[0] : rule.value
      if (!target) return true // No target value — incomplete rule
      // For comma-separated CRM fields: exact item match (inverted)
      if (isCommaSeparatedField(rule)) {
        const items = splitCommaValues(rawValue)
        return !items.some(
          (item) => item.toLowerCase() === target.toLowerCase(),
        )
      }
      // For all other fields: substring match (inverted)
      return !rawValue.toLowerCase().includes(target.toLowerCase())
    }

    case 'in': {
      if (!rawValue) return false
      const targets = Array.isArray(rule.value) ? rule.value : [rule.value]
      const validTargets = targets.filter((t) => t != null && t !== '')
      if (validTargets.length === 0) return false // No targets — incomplete rule
      // For comma-separated CRM fields, check if ANY item in the contact's field
      // matches ANY of the target values
      if (isCommaSeparatedField(rule)) {
        const items = splitCommaValues(rawValue)
        return items.some((item) =>
          validTargets.some((t) => item.toLowerCase() === t.toLowerCase()),
        )
      }
      return validTargets.some(
        (t) => rawValue.toLowerCase() === (t || '').toLowerCase(),
      )
    }

    default:
      return false
  }
}

export function filterContactsByRules(
  contacts: CrmContact[],
  rules: AudienceRule[],
  logic: 'AND' | 'OR',
): CrmContact[] {
  if (rules.length === 0) return []

  return contacts.filter((contact) => {
    if (logic === 'AND') {
      return rules.every((rule) => matchesRule(contact, rule))
    } else {
      return rules.some((rule) => matchesRule(contact, rule))
    }
  })
}

// ── Helpers ──

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 8)
}

export function createEmptyRule(): AudienceRule {
  return {
    id: generateId(),
    field: '',
    fieldSource: 'standard',
    operator: 'equals',
    value: '',
  }
}

// ── Campaign Integration ──

/**
 * Apply one or more saved audiences to a list of contacts.
 * Each audience's rules are applied independently, and results are merged (OR logic between audiences).
 * Within each audience, rules use the audience's own logic (AND/OR).
 *
 * @param contacts - Full list of contacts to filter
 * @param audienceIds - IDs of saved audiences to apply
 * @param team - Current workspace team name (for loading audiences)
 * @returns Contacts matching ANY of the selected audiences
 */
export function applyAudiencesToContacts(
  contacts: CrmContact[],
  audiences: SavedAudience[],
): CrmContact[] {
  if (audiences.length === 0 || contacts.length === 0) return []

  const matchedIds = new Set<string>()
  const matchedContacts: CrmContact[] = []

  for (const audience of audiences) {
    // Skip audiences with no valid rules
    const validRules = audience.rules.filter((r) => r.field)
    if (validRules.length === 0) continue

    const matches = filterContactsByRules(contacts, validRules, audience.logic)
    for (const contact of matches) {
      if (contact.id && !matchedIds.has(contact.id)) {
        matchedIds.add(contact.id)
        matchedContacts.push(contact)
      }
    }
  }

  return matchedContacts
}

/**
 * Get a human-readable summary of an audience's rules for display.
 */
export function getAudienceRuleSummary(audience: SavedAudience): string {
  const count = audience.rules.length
  if (count === 0) return 'No rules defined'
  const payloadRules = audience.rules.filter(
    (r) => r.fieldSource === 'payload',
  ).length
  const standardRules = count - payloadRules
  const parts: string[] = []
  if (standardRules > 0)
    parts.push(`${standardRules} CRM filter${standardRules !== 1 ? 's' : ''}`)
  if (payloadRules > 0)
    parts.push(
      `${payloadRules} form data filter${payloadRules !== 1 ? 's' : ''}`,
    )
  return parts.join(' + ') + ` (${audience.logic})`
}
